import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Checkbox,
  message,
  Descriptions,
  Typography,
  Alert,
  Statistic,
  Badge,
  Tooltip,
  Popconfirm,
  Dropdown,
  Progress
} from 'antd';
import { Tabs } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  PrinterOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SettingOutlined,
  InboxOutlined,
  TruckOutlined,
  WarningOutlined,
  ScanOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../../components/table/useDraggableColumns';
import moment from 'moment';
import type { 
  OutboundOrder, 
  BatchPickingList, 
  ConsolidatedPickingItem,
  PickingFilters 
} from '../../types/picking';
import BarcodeScanningInterface from '../BarcodeScanningInterface';
import WaybillManagement from '../WaybillManagement';
import apiService from '../../../../services/apiService';
// import { PDFService } from '../../../../services/pdfService';
import { generatePickingOrderPDF } from '../../../../utils/pdfUtils';
import { useAuthStore } from '../../../../stores/authStore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

type AnyRecord = Record<string, any>;

interface PickingManagementProps {
  onClose?: () => void;
}

const PickingManagement: React.FC<PickingManagementProps> = ({ onClose: _onClose }) => {
  const { user } = useAuthStore();
  // mark prop as read to satisfy linter
  void _onClose;
  // 預設顯示揀貨單管理，並提供開關以隱藏出庫訂單頁籤與內容
  const [activeTab, setActiveTab] = useState<'orders' | 'picking' | 'scanning' | 'waybill'>(user?.role != 'PICK_SCANNING' ? 'picking' : 'scanning');
  const showOutboundOrders = false; // 如需恢復，改為 true 即可
  const [outboundOrders, setOutboundOrders] = useState<OutboundOrder[]>([]);
  const [batchPickingLists, setBatchPickingLists] = useState<BatchPickingList[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [filters, setFilters] = useState<PickingFilters>({});
  const [loading, setLoading] = useState(false);
  const [kpi, setKpi] = useState<{ processing: number; allocating: number; verifying: number }>({ processing: 0, allocating: 0, verifying: 0 });
  const [scanHistoryClearKey, setScanHistoryClearKey] = useState<number>(0);

  // 出庫訂單表格排序狀態
  const [outboundSortField, setOutboundSortField] = useState<string>('');
  const [outboundSortOrder, setOutboundSortOrder] = useState<'asc' | 'desc' | ''>('');

  // 批量揀貨表格排序狀態
  const [pickingSortField, setPickingSortField] = useState<string>('');
  const [pickingSortOrder, setPickingSortOrder] = useState<'asc' | 'desc' | ''>('');

  // KPI刷新函数
  const refreshKpi = async () => {
    try {
      const resp: any = await apiService.getPickingKPI();
      const d = resp?.data || resp;
      if (d) setKpi({
        processing: Number(d.processing || 0),
        allocating: Number(d.allocating || 0),
        verifying: Number(d.verifying || 0)
      });
    } catch {}
  };

  // 載入 KPI 統計
  useEffect(() => {
    // 初始加载
    refreshKpi();

    // 每30秒自动刷新一次KPI数据
    const intervalId = setInterval(refreshKpi, 30000);

    // 组件卸载时清除定时器
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // 出庫訂單表格列配置狀態
  const [outboundColumnSettings, setOutboundColumnSettings] = useState(() => {
    const saved = localStorage.getItem('picking-outbound-columns-config');
    const version = localStorage.getItem('picking-outbound-columns-version');
    
    if (version !== '2024-01-v2' || !saved) {
      const defaultConfig = {
        select: { visible: true, order: 0 },
        orderNumber: { visible: true, order: 1 },
        customer: { visible: true, order: 2 },
        priority: { visible: true, order: 3 },
        totalQuantity: { visible: true, order: 4 },
        status: { visible: true, order: 5 },
        shippingDate: { visible: true, order: 6 },
        warehouse: { visible: false, order: 7 },
        actions: { visible: true, order: 8 }
      } as AnyRecord;
      localStorage.setItem('picking-outbound-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('picking-outbound-columns-version', '2024-01-v2');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved as string);
    } catch {
      const fallback = {
        select: { visible: true, order: 0 },
        orderNumber: { visible: true, order: 1 },
        customer: { visible: true, order: 2 },
        priority: { visible: true, order: 3 },
        totalQuantity: { visible: true, order: 4 },
        status: { visible: true, order: 5 },
        shippingDate: { visible: true, order: 6 },
        warehouse: { visible: false, order: 7 },
        actions: { visible: true, order: 8 }
      } as AnyRecord;
      return fallback;
    }
  });

  // 批量揀貨表格列配置狀態
  const [pickingColumnSettings, setPickingColumnSettings] = useState(() => {
    const saved = localStorage.getItem('picking-batch-columns-config');
    const version = localStorage.getItem('picking-batch-columns-version');
    
    if (version !== '2025-10-v1' || !saved) {
      const defaultConfig = {
        batchNumber: { visible: true, order: 0 },
        barcode: { visible: true, order: 1 },
        status: { visible: true, order: 2 },
        totalOrders: { visible: true, order: 3 },
        totalQuantity: { visible: true, order: 4 },
        priority: { visible: true, order: 5 },
        picker: { visible: true, order: 6 },
        location: { visible: true, order: 7 },
        progress: { visible: true, order: 8 },
        scheduledDate: { visible: true, order: 9 },
        createdAt: { visible: true, order: 10 },
        actions: { visible: true, order: 11 }
      } as AnyRecord;
      localStorage.setItem('picking-batch-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('picking-batch-columns-version', '2025-10-v1');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved as string);
    } catch {
      const fallback = {
        batchNumber: { visible: true, order: 0 },
        barcode: { visible: true, order: 1 },
        status: { visible: true, order: 2 },
        totalOrders: { visible: true, order: 3 },
        totalQuantity: { visible: true, order: 4 },
        priority: { visible: true, order: 5 },
        picker: { visible: true, order: 6 },
        location: { visible: true, order: 7 },
        progress: { visible: true, order: 8 },
        scheduledDate: { visible: true, order: 9 },
        createdAt: { visible: true, order: 10 },
        actions: { visible: true, order: 11 }
      } as AnyRecord;
      return fallback;
    }
  });

  // 排序輔助
  const handleOutboundColumnSort = (columnKey: string) => {
    if (outboundSortField === columnKey) {
      setOutboundSortOrder(outboundSortOrder === 'asc' ? 'desc' : outboundSortOrder === 'desc' ? '' : 'asc');
      if (outboundSortOrder === 'desc') {
        setOutboundSortField('');
      }
    } else {
      setOutboundSortField(columnKey);
      setOutboundSortOrder('asc');
    }
  };

  const getOutboundSortIcon = (columnKey: string) => {
    if (outboundSortField !== columnKey) return null;
    return outboundSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  const handlePickingColumnSort = (columnKey: string) => {
    if (pickingSortField === columnKey) {
      setPickingSortOrder(pickingSortOrder === 'asc' ? 'desc' : pickingSortOrder === 'desc' ? '' : 'asc');
      if (pickingSortOrder === 'desc') {
        setPickingSortField('');
      }
    } else {
      setPickingSortField(columnKey);
      setPickingSortOrder('asc');
    }
  };

  const getPickingSortIcon = (columnKey: string) => {
    if (pickingSortField !== columnKey) return null;
    return pickingSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // Modal states
  const [generatePickingVisible, setGeneratePickingVisible] = useState(false);
  const [pickingDetailVisible, setPickingDetailVisible] = useState(false);
  const [selectedPicking, setSelectedPicking] = useState<BatchPickingList | null>(null);
  // 列設置下拉菜單可見狀態
  const [outboundColumnSettingsVisible, setOutboundColumnSettingsVisible] = useState(false);
  const [pickingColumnSettingsVisible, setPickingColumnSettingsVisible] = useState(false);
  const [selectedPickingForScan, setSelectedPickingForScan] = useState<BatchPickingList | null>(null);
  // 分頁狀態
  const [outboundCurrentPage, setOutboundCurrentPage] = useState<number>(1);
  const [outboundPageSize, setOutboundPageSize] = useState<number>(10);
  const [outboundTotal, setOutboundTotal] = useState<number>(0);
  const [batchCurrentPage, setBatchCurrentPage] = useState<number>(1);
  const [batchPageSize, setBatchPageSize] = useState<number>(10);
  const [batchTotal, setBatchTotal] = useState<number>(0);

  useEffect(() => {
    if (activeTab === 'orders') {
      loadData('orders');
    } else if (activeTab === 'picking') {
      loadData('picking');
    }
  }, [activeTab, outboundCurrentPage, outboundPageSize, batchCurrentPage, batchPageSize, filters.searchKeyword, (filters as AnyRecord).status, (filters as AnyRecord).dateRange, (filters as AnyRecord).salesChannel]);

  const loadData = async (scope: 'orders' | 'picking' | 'both' = 'both') => {
    setLoading(true);
    try {
      const normalizeStatusCode = (s: any): OutboundOrder['status'] => {
        const v = (s ?? '').toString().toLowerCase();
        if (['pending', 'pickup_pending', 'waiting'].includes(v)) return 'waiting_shipment';
        if (['processing'].includes(v)) return 'processing';
        if (['allocating', 'picking'].includes(v)) return 'picking';
        if (['verifying', 'checking'].includes(v)) return 'checking';
        if (['packed', 'packing_completed'].includes(v)) return 'packed';
        if (['shipped', 'received', 'delivered'].includes(v)) return 'delivered';
        if (['return_requested', 'return_processing'].includes(v)) return 'processing';
        if (['return_completed'].includes(v)) return 'delivered';
        return 'waiting_shipment';
      };
      // 1) 讀取出庫訂單（後端）
      if (scope !== 'picking') {
        const salesChannelArr = (filters as AnyRecord)?.salesChannel as string[] | undefined;
        const salesChannel = Array.isArray(salesChannelArr) && salesChannelArr.length > 0 ? salesChannelArr.join(',') : undefined;
        const resp: any = await apiService.getOutboundOrders({ 
          page: outboundCurrentPage, 
          limit: outboundPageSize,
          search: (filters as AnyRecord)?.searchKeyword || undefined,
          salesChannel
        });
        const rows: any[] = resp?.data || resp || [];
        const mapped: OutboundOrder[] = rows.map((r: any): OutboundOrder => ({
          id: String(r.id),
          orderNumber: r.orderNumber,
          customerId: r.customerId,
          customerName: r.customerName,
          status: normalizeStatusCode(r.status),
          priority: (r.priority || 'normal') as OutboundOrder['priority'],
          warehouseId: r.warehouseId,
          warehouseName: r.warehouseName,
          scheduledDate: r.scheduledDate ? new Date(r.scheduledDate) : new Date(),
          createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
          updatedAt: r.updatedAt ? new Date(r.updatedAt) : new Date(),
          totalQuantity: Number(r.totalQuantity || 0),
          deliveryAddress: r.deliveryAddress,
          deliveryMethod: r.deliveryMethod,
          deliveryCompany: r.deliveryCompany,
          trackingNumber: r.trackingNumber,
          salesChannel: r.salesChannel,
          items: [],
          notes: r.notes,
          hasStockIssues: Boolean(r.hasStockIssues)
        }));
        setOutboundOrders(mapped);
        const total = resp?.pagination?.total ?? resp?.total ?? resp?.totalCount ?? resp?.count ?? resp?.meta?.total ?? resp?.meta?.pagination?.total ?? mapped.length;
        setOutboundTotal(Number(total) || 0);
      }

      // 2) 讀取批量揀貨清單（後端）
      if (scope !== 'orders') {
        const dateRange: any = (filters as AnyRecord)?.dateRange;
        const dateFrom = dateRange && dateRange[0] ? (dateRange[0].startOf ? dateRange[0].startOf('day').toISOString() : new Date(dateRange[0]).toISOString()) : undefined;
        const dateTo = dateRange && dateRange[1] ? (dateRange[1].endOf ? dateRange[1].endOf('day').toISOString() : new Date(dateRange[1]).toISOString()) : undefined;
        const statusArr = (filters as AnyRecord)?.status as string[] | undefined;
        const status = Array.isArray(statusArr) && statusArr.length > 0 ? statusArr[0] : undefined;
        const pickResp: any = await apiService.getPickingLists({
          page: batchCurrentPage,
          limit: batchPageSize,
          search: (filters as AnyRecord)?.searchKeyword || undefined,
          status,
          dateFrom,
          dateTo,
          sortField: 'created_at',
          sortOrder: 'desc'
        });
        const pickRows: any[] = pickResp?.data || pickResp || [];
        const pickMapped: BatchPickingList[] = pickRows.map((r: AnyRecord) => ({
          id: String(r.id),
          batchPickingNumber: r.batchPickingNumber || r.batch_picking_no,
          batchPickingBarcode: r.batchPickingBarcode || r.batch_picking_barcode || '',
          status: (r.status || 'processing') as BatchPickingList['status'],
          scheduledDate: r.scheduledDate ? new Date(r.scheduledDate) : new Date(),
          createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
          updatedAt: r.updatedAt ? new Date(r.updatedAt) : new Date(),
          outboundOrders: [],
          pickOrderNumbers: r.pickOrderNumbers || [],
          totalOrders: Number(r.totalOrders || 0),
          totalItems: Number(r.totalItems || 0),
          totalQuantity: Number(r.totalQuantity || 0),
          locationFrom: r.locationFrom || '',
          locationTo: r.locationTo || '',
          consolidatedItems: [],
          assignedTo: r.assignedTo || r.picker || undefined,
          discrepancies: []
        }));
        setBatchPickingLists(pickMapped);
        const pickTotal = pickResp?.pagination?.total ?? pickResp?.total ?? pickResp?.totalCount ?? pickResp?.count ?? pickResp?.meta?.total ?? pickResp?.meta?.pagination?.total ?? pickMapped.length;
        setBatchTotal(pickTotal);
      }
    } finally {
      setLoading(false);
    }
  };

  // 導出功能 - 客戶端CSV生成
  const handleExportExcel = () => {
    try {
      // 根據當前tab決定導出內容
      let csvContent = '';
      let filename = '';
      
      if (activeTab === 'orders') {
        csvContent = generateOutboundOrdersCSVContent(filteredOutboundOrders);
        filename = `picking_outbound_orders_export_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (activeTab === 'picking') {
        csvContent = generateBatchPickingListsCSVContent(filteredBatchPickingLists);
        filename = `picking_batch_lists_export_${new Date().toISOString().split('T')[0]}.csv`;
      }
      
      if (csvContent) {
        // 創建Blob並下載
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        message.success('拾貨管理數據已成功導出');
      }
    } catch (error) {
      console.error('導出失敗:', error);
      message.error('導出失敗，請稍後重試');
    }
  };

  // 生成出庫訂單CSV內容
  const generateOutboundOrdersCSVContent = (data: OutboundOrder[]) => {
    const headers = [
      '訂單號', '拾貨單號', '拾貨條碼', '客戶ID', '客戶名稱', '狀態', '優先級', '倉庫',
      '預定日期', '總數量', '配送地址', '配送方式', '配送公司', '追蹤號', '銷售渠道', '備註', '創建時間', '更新時間'
    ];

    const rows = data.map(item => [
      item.orderNumber || '',
      item.pickOrderNumber || '',
      item.pickOrderBarcode || '',
      item.customerId || '',
      item.customerName || '',
      item.status || '',
      item.priority || '',
      item.warehouseName || '',
      item.scheduledDate ? `\u200B${new Date(item.scheduledDate).toISOString().split('T')[0]}` : '',
      item.totalQuantity || '',
      item.deliveryAddress || '',
      item.deliveryMethod || '',
      item.deliveryCompany || '',
      item.trackingNumber || '',
      item.salesChannel || '',
      item.notes || '',
      item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : '',
      item.updatedAt ? `\u200B${new Date(item.updatedAt).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 生成批次拾貨清單CSV內容
  const generateBatchPickingListsCSVContent = (data: BatchPickingList[]) => {
    const headers = [
      '批次號', '拾貨員', '狀態', '總訂單', '總項目', '總數量', '起始位置', '目標位置', '創建時間'
    ];

    const rows = data.map((item: BatchPickingList) => [
      item.batchPickingNumber || '',
      item.assignedTo || '',
      item.status || '',
      item.totalOrders || '',
      item.totalItems || '',
      item.totalQuantity || '',
      item.locationFrom || '',
      item.locationTo || '',
      item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const handleStatusChange = async (orderIds: string[], newStatus: string) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate pick order numbers and barcodes for orders moving to processing
      if (newStatus === 'processing') {
        const updatedOrders = outboundOrders.map(order => {
          if (orderIds.includes(order.id)) {
            return {
              ...order,
              status: 'processing' as OutboundOrder['status'],
              pickOrderNumber: `PICK-${order.orderNumber}`,
              pickOrderBarcode: `BC-${order.orderNumber}`,
              updatedAt: new Date()
            };
          }
          return order;
        });
        setOutboundOrders(updatedOrders);
        message.success(`成功將 ${orderIds.length} 個訂單狀態更新為 ${getStatusText(newStatus)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatchPicking = async (values: any) => {
    setLoading(true);
    try {
      // 後端負責生成批次號與條碼
      // 收集所選訂單號
      const selectedOrderData = outboundOrders.filter(o => selectedOrders.includes(o.id));
      const orderNumbers = selectedOrderData.map(o => o.orderNumber).filter(Boolean);

      const createdResp: any = await apiService.createPickingList({
        status: 'processing',
        scheduledDate: new Date().toISOString(),
        priority: 'normal',
        locationFrom: values.locationFrom || '主倉庫',
        locationTo: values.locationTo || '打包區A',
        totalOrders: selectedOrders.length,
        orderNumbers,
      });
      const created = (createdResp && (createdResp.data || createdResp)) as any;
      // 先為被選中的訂單生成/補全拣貨單號與條碼，並標記為 processing，讓列表即時顯示
      const updatedOrders = outboundOrders.map(order => {
        if (!selectedOrders.includes(order.id)) return order;
        const hasPickIds = Boolean(order.pickOrderNumber && order.pickOrderBarcode);
        return {
          ...order,
          status: 'processing',
          pickOrderNumber: hasPickIds ? order.pickOrderNumber : `PICK-${order.orderNumber}`,
          pickOrderBarcode: hasPickIds ? order.pickOrderBarcode : `BC-${order.orderNumber}`,
          updatedAt: new Date()
        };
      });
      setOutboundOrders(updatedOrders as OutboundOrder[]);

      // 重新計算被選中的訂單（更新後）
      const selectedOrderDataAfter = (updatedOrders as OutboundOrder[]).filter((order: OutboundOrder) => selectedOrders.includes(order.id));

      // Generate batch picking list
      // 用本地回顯讓使用者立即看到
      
      // Consolidate items from multiple orders
      const itemMap = new Map<string, ConsolidatedPickingItem>();
      
      selectedOrderDataAfter.forEach((order: OutboundOrder) => {
        order.items.forEach((item: any) => {
          const key = `${item.productId}-${item.locationFrom}-${item.locationTo}`;
          if (itemMap.has(key)) {
            const existing = itemMap.get(key)!;
            existing.totalQuantity += item.requestedQuantity;
            existing.sourceOrders.push({
              pickOrderNumber: order.pickOrderNumber!,
              quantity: item.requestedQuantity
            });
            if (item.lotNumber && !existing.lotNumbers.includes(item.lotNumber)) {
              existing.lotNumbers.push(item.lotNumber);
            }
            if (item.serialNumber && !existing.serialNumbers.includes(item.serialNumber)) {
              existing.serialNumbers.push(item.serialNumber);
            }
            if (item.expirationDate) {
              existing.expirationDates.push(item.expirationDate);
            }
          } else {
            itemMap.set(key, {
              productId: item.productId,
              productSku: item.productSku,
              productNameEn: item.productNameEn,
              productNameCn: item.productNameCn,
              productBarcode: item.productBarcode,
              totalQuantity: item.requestedQuantity,
              pickedQuantity: 0,
              confirmedQuantity: 0,
              locationFrom: item.locationFrom,
              locationTo: item.locationTo,
              lotNumbers: item.lotNumber ? [item.lotNumber] : [],
              serialNumbers: item.serialNumber ? [item.serialNumber] : [],
              expirationDates: item.expirationDate ? [item.expirationDate] : [],
              sourceOrders: [{
                pickOrderNumber: order.pickOrderNumber!,
                quantity: item.requestedQuantity
              }],
              hasDiscrepancy: false
            });
          }
        });
      });

      // 後端生成成功後，刷新列表並跳轉到「揀貨單管理」
      await loadData('picking');
      setActiveTab('picking');
      setGeneratePickingVisible(false);
      setSelectedOrders([]);
      const showNo = created?.batchPickingNumber || created?.batch_picking_no;
      message.success(`批量揀貨單 ${showNo || ''} 生成成功！`);
      
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'default',
      'waiting_shipment': 'orange',
      'processing': 'blue',
      'picking': 'purple',
      'checking': 'cyan',
      'packed': 'green',
      'delivered': 'success'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      'pending': '待處理',
      'waiting_shipment': '待發貨',
      'processing': '處理中',
      'picking': '配貨中',
      'checking': '核對中',
      'packed': '已打包',
      'delivered': '已送達'
    };
    return texts[status] || status;
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      'low': 'default',
      'normal': 'blue',
      'high': 'orange',
      'urgent': 'red'
    };
    return colors[priority] || 'default';
  };

  const getPriorityText = (priority: string) => {
    const texts: Record<string, string> = {
      'low': '低',
      'normal': '普通',
      'high': '高',
      'urgent': '緊急'
    };
    return texts[priority] || priority;
  };

  // 定義所有可用的出庫訂單列
  const allOutboundColumns: AnyRecord = {
    select: {
      title: '選擇',
      key: 'select',
      width: 60,
      render: (_: unknown, record: OutboundOrder) => {
        const disabled = (record.status !== 'waiting_shipment' && record.status !== 'processing') || record.hasStockIssues;
        const checkbox = (
          <Checkbox
            checked={selectedOrders.includes(record.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedOrders([...selectedOrders, record.id]);
              } else {
                setSelectedOrders(selectedOrders.filter(id => id !== record.id));
              }
            }}
            disabled={disabled}
          />
        );
        return record.hasStockIssues ? (
          <Tooltip title="庫存不足，無法加入揀貨">
            <span>{checkbox}</span>
          </Tooltip>
        ) : checkbox;
      }
    },
    orderNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('orderNumber')}
        >
          訂單編號
          {getOutboundSortIcon('orderNumber')}
        </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 140,
      sorter: true,
      render: (text: string, record: OutboundOrder) => (
        <div>
          <div style={{ fontWeight: 'bold', color: record.hasStockIssues ? '#ff4d4f' : undefined }}>
            {text}
            {record.hasStockIssues && (
              <Tooltip title="庫存不足">
                <WarningOutlined style={{ color: '#ff4d4f', marginLeft: 6 }} />
              </Tooltip>
            )}
          </div>
          {record.pickOrderNumber && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              揀貨單: {record.pickOrderNumber}
            </div>
          )}
        </div>
      )
    },
    customer: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('customerName')}
        >
          客戶
          {getOutboundSortIcon('customerName')}
        </div>
      ),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 150,
      sorter: true
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('status')}
        >
          狀態
          {getOutboundSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    priority: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('priority')}
        >
          優先級
          {getOutboundSortIcon('priority')}
        </div>
      ),
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: true,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityText(priority)}
        </Tag>
      )
    },
    warehouse: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('warehouseName')}
        >
          倉庫
          {getOutboundSortIcon('warehouseName')}
        </div>
      ),
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      width: 100,
      sorter: true
    },
    totalQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('totalQuantity')}
        >
          總數量
          {getOutboundSortIcon('totalQuantity')}
        </div>
      ),
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 80,
      align: 'right' as 'right',
      sorter: true
    },
    shippingDate: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleOutboundColumnSort('scheduledDate')}
        >
          計劃日期
          {getOutboundSortIcon('scheduledDate')}
        </div>
      ),
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      width: 110,
      sorter: true,
      render: (date: Date) => moment(date).format('YYYY-MM-DD')
    },
    actions: {
      title: '條碼',
      key: 'barcode',
      width: 80,
      render: (_: any, record: OutboundOrder) => (
        record.pickOrderBarcode ? (
          <Tooltip title={record.pickOrderBarcode}>
            <BarcodeOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
          </Tooltip>
        ) : (
          <span style={{ color: '#ccc' }}>-</span>
        )
      )
    }
  };

  // 根據列配置生成顯示的出庫訂單列
  const outboundOrderColumns: ColumnsType<OutboundOrder> = Object.entries(outboundColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allOutboundColumns[key as keyof typeof allOutboundColumns];
      if (!column) {
        console.warn(`Missing outbound column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 出庫訂單表支持拖拽列排序（禁用 actions/select）
  const { columns: draggableOutboundColumns, order: outboundOrder } = useDraggableColumns<OutboundOrder>({
    columns: outboundOrderColumns as any,
    disableDragKeys: ['actions', 'select'],
    storageKey: 'picking-outbound-table',
  });

  useEffect(() => {
    if (!outboundOrder || outboundOrder.length === 0) return;
    setOutboundColumnSettings((prev: any) => {
      const next: any = { ...prev };
      outboundOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!outboundOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(outboundOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('picking-outbound-columns-config', JSON.stringify(next));
        localStorage.setItem('picking-outbound-columns-version', '2024-01-v2');
      } catch {}
      return next;
    });
  }, [outboundOrder]);

  // 處理出庫訂單列可見性切換
  const handleOutboundColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...outboundColumnSettings,
      [columnKey]: { ...outboundColumnSettings[columnKey], visible }
    };
    setOutboundColumnSettings(newSettings);
    localStorage.setItem('picking-outbound-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('picking-outbound-columns-version', '2024-01-v2');
  };

  // 處理批量揀貨列可見性切換
  const handlePickingColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...pickingColumnSettings,
      [columnKey]: { ...pickingColumnSettings[columnKey], visible }
    };
    setPickingColumnSettings(newSettings);
    localStorage.setItem('picking-batch-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('picking-batch-columns-version', '2025-02-v1');
  };

  // 出庫訂單列設置菜單
  const outboundColumnSettingsMenu = (
    <div style={{
      background: '#fff',
      borderRadius: '2px',
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>列設置</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
      {Object.entries(allOutboundColumns).map(([key, column]) => (
          <div
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOutboundColumnVisibilityChange(key, !(outboundColumnSettings[key]?.visible ?? true));
            }}
          >
          <Checkbox
            checked={outboundColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleOutboundColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
          >
            {typeof column.title === 'string' ? column.title : 
             key === 'select' ? '選擇' :
             key === 'orderNumber' ? '訂單編號' :
             key === 'customer' ? '客戶' :
             key === 'priority' ? '優先級' :
             key === 'totalQuantity' ? '總數量' :
             key === 'status' ? '狀態' :
             key === 'shippingDate' ? '計劃日期' :
             key === 'warehouse' ? '倉庫' :
             key === 'actions' ? '條碼' : key}
          </Checkbox>
          </div>
      ))}
      </div>
    </div>
  );





  // 定義所有可用的批量揀貨列
  const allBatchPickingColumns: AnyRecord = {
    batchNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('batchPickingNumber')}
        >
          批次編號
          {getPickingSortIcon('batchPickingNumber')}
        </div>
      ),
      dataIndex: 'batchPickingNumber',
      key: 'batchPickingNumber',
      width: 160,
      fixed: 'left' as 'left',
      sorter: true,
      render: (text: string) => (
        <div style={{ fontWeight: 'bold' }}>{text}</div>
      )
    },
    barcode: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('batchPickingBarcode')}
        >
          條碼
          {getPickingSortIcon('batchPickingBarcode')}
        </div>
      ),
      dataIndex: 'batchPickingBarcode',
      key: 'batchPickingBarcode',
      width: 160,
      fixed: 'left' as 'left',
      sorter: true,
      render: (barcode: string) => (
        barcode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title={barcode}>
              <BarcodeOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
            </Tooltip>
            <span style={{ fontFamily: 'monospace' }}>{barcode}</span>
          </div>
        ) : '-'
      )
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('status')}
        >
          狀態
          {getPickingSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    totalOrders: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('totalOrders')}
        >
          訂單數量
          {getPickingSortIcon('totalOrders')}
        </div>
      ),
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      width: 80,
      align: 'center' as 'center',
      sorter: true
    },
    totalQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('totalQuantity')}
        >
          總數量
          {getPickingSortIcon('totalQuantity')}
        </div>
      ),
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 80,
      align: 'right' as 'right',
      sorter: true
    },
    priority: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('priority')}
        >
          優先級
          {getPickingSortIcon('priority')}
        </div>
      ),
      key: 'priority',
      width: 80,
      sorter: true,
      render: (_: any, record: BatchPickingList) => {
        // 根據包含的訂單計算最高優先級
        const orders = record.outboundOrders || [];
        const priorities = orders.map((order: OutboundOrder) => order.priority);
        const highestPriority = priorities.includes('urgent') ? 'urgent' :
                               priorities.includes('high') ? 'high' :
                               priorities.includes('normal') ? 'normal' : 'low';
        return (
          <Tag color={getPriorityColor(highestPriority)}>
            {getPriorityText(highestPriority)}
          </Tag>
        );
      }
    },
    picker: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('assignedTo')}
        >
          揀貨員
          {getPickingSortIcon('assignedTo')}
        </div>
      ),
      dataIndex: 'assignedTo',
      key: 'picker',
      width: 100,
      sorter: true,
      render: (picker: any) => picker || '-'
    },
    location: {
      title: '位置',
      key: 'location',
      width: 150,
      render: (_: any, record: BatchPickingList) => (
        <div>
          <div style={{ fontSize: '12px' }}>從: {record.locationFrom}</div>
          <div style={{ fontSize: '12px' }}>到: {record.locationTo}</div>
        </div>
      )
    },
    scheduledDate: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('scheduledDate')}
        >
          計劃日期
          {getPickingSortIcon('scheduledDate')}
        </div>
      ),
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      width: 110,
      sorter: true,
      render: (date: any) => moment(date).format('YYYY-MM-DD')
    },
    createdAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickingColumnSort('createdAt')}
        >
          創建時間
          {getPickingSortIcon('createdAt')}
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: true,
      render: (date: any) => moment(date).format('YYYY-MM-DD HH:mm')
    },
    progress: {
      title: '進度',
      key: 'progress',
      width: 120,
      render: (_: unknown, record: BatchPickingList) => {
        const totalQty = record.totalQuantity;
        const pickedQty = record.consolidatedItems.reduce((sum: number, item: any) => sum + (item.pickedQuantity || 0), 0);
        const confirmedQty = record.consolidatedItems.reduce((sum: number, item: any) => sum + (item.confirmedQuantity || 0), 0);
        const pickedPercent = totalQty > 0 ? Math.round((pickedQty / totalQty) * 100) : 0;
        const confirmedPercent = totalQty > 0 ? Math.round((confirmedQty / totalQty) * 100) : 0;
        
        return (
          <div>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
              揀貨: {pickedPercent}%
            </div>
            <Progress percent={pickedPercent} size="small" status="active" />
            <div style={{ fontSize: '12px', marginTop: '4px', marginBottom: '4px' }}>
              確認: {confirmedPercent}%
            </div>
            <Progress percent={confirmedPercent} size="small" status="success" />
          </div>
        );
      }
    },
    actions: {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right' as 'right',
      render: (_: unknown, record: BatchPickingList) => (
        <Space>
          <Button 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => {
              setSelectedPicking(record);
              setPickingDetailVisible(true);
            }}
          >
            詳情
          </Button>
          <Button 
            size="small" 
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => handlePrintPickingList(record)}
          >
            列印
          </Button>
          <Button
              size="small"
              icon={<PrinterOutlined />}
              onClick={async (e) => {
                try { e?.preventDefault(); e?.stopPropagation(); } catch {}
                try {
                  const data = record;
                  const { blob, fileName } = await apiService.downloadPrintLabelCombined({ id: data.id });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = fileName;
                  a.style.display = 'none';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                  message.success('Pick Label PDF 下載成功');
                } catch (e) {
                  console.error(e);
                  message.error('下載 Pick Label PDF 失敗');
                }
              }}
          >
            Pick Label
          </Button>
        </Space>
      )
    }
  };

  // 批量揀貨列設置菜單
  const pickingColumnSettingsMenu = (
    <div style={{
      background: '#fff',
      borderRadius: '2px',
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>列設置</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
      {Object.entries(allBatchPickingColumns).map(([key, column]) => (
          <div
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handlePickingColumnVisibilityChange(key, !((pickingColumnSettings as AnyRecord)[key]?.visible ?? true));
            }}
          >
          <Checkbox
            checked={(pickingColumnSettings as AnyRecord)[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handlePickingColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
          >
            {typeof (column as AnyRecord).title === 'string' ? (column as AnyRecord).title : 
             key === 'batchNumber' ? '批次編號' :
             key === 'barcode' ? '條碼' :
             key === 'status' ? '狀態' :
             key === 'totalOrders' ? '訂單數量' :
             key === 'totalQuantity' ? '總數量' :
             key === 'priority' ? '優先級' :
             key === 'picker' ? '揀貨員' :
             key === 'location' ? '位置' :
             key === 'scheduledDate' ? '計劃日期' :
             key === 'createdAt' ? '創建時間' :
             key === 'progress' ? '進度' :
             key === 'actions' ? '操作' : key}
          </Checkbox>
          </div>
      ))}
      </div>
    </div>
  );



  // 根據列配置生成顯示的批量揀貨列
  const batchPickingColumns: ColumnsType<BatchPickingList> = (() => {
    // 先按配置生成 [key, column] 對
    const pairs = Object.entries(pickingColumnSettings)
      .filter(([_, config]: [string, any]) => config.visible)
      .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
      .map(([key]) => {
        const column = allBatchPickingColumns[key as keyof typeof allBatchPickingColumns];
        if (!column) {
          console.warn(`Missing batch picking column definition for key: ${key}`);
        }
        return { key, column };
      })
      .filter((p): p is { key: string; column: any } => Boolean(p && p.column));

    // 固定在最左側的列（順序固定為 batchNumber -> barcode）
    const leftKeys = ['batchNumber', 'barcode'];
    const leftColumns = leftKeys
      .map(k => pairs.find(p => p.key === k)?.column)
      .filter(Boolean);

    // 中間其餘列（排除左側和 actions）
    const middleColumns = pairs
      .filter(p => !leftKeys.includes(p.key) && p.key !== 'actions')
      .map(p => p.column);

    // 操作列固定在最右
    const actionsColumn = pairs.find(p => p.key === 'actions')?.column;

    return [...leftColumns, ...middleColumns, ...(actionsColumn ? [actionsColumn] : [])] as any;
  })();

  // 批量揀貨表支持拖拽列排序（禁用 actions）
  const { columns: draggableBatchPickingColumns, order: batchOrder } = useDraggableColumns<BatchPickingList>({
    columns: batchPickingColumns as any,
    // 禁止拖拽固定列與操作列
    disableDragKeys: ['batchPickingNumber', 'batchPickingBarcode', 'actions'],
    storageKey: 'picking-batch-table',
  });

  useEffect(() => {
    if (!batchOrder || batchOrder.length === 0) return;
    setPickingColumnSettings((prev: any) => {
      const next: any = { ...prev };
      batchOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!batchOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(batchOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('picking-batch-columns-config', JSON.stringify(next));
        localStorage.setItem('picking-batch-columns-version', '2025-10-v1');
      } catch {}
      return next;
    });
  }, [batchOrder]);

  const handlePrintPickingList = async (pickingList: BatchPickingList) => {
    // 使用客戶端PDF生成（與inbound、waybill相同的方式）
    try {
      await generatePickingOrderPDF(pickingList.batchPickingNumber);
    } catch (err) {
      console.error('生成揀貨單PDF失敗:', err);
      message.error('無法生成揀貨單 PDF，請稍後重試');
    }
  };

  

  // 出庫訂單不再做本地過濾，直接用後端結果
  const filteredOutboundOrders = outboundOrders;

  const filteredBatchPickingLists = batchPickingLists.filter((picking) => {
    if (filters.searchKeyword) {
      const keyword = filters.searchKeyword.toLowerCase();
      if (!picking.batchPickingNumber.toLowerCase().includes(keyword) &&
          !picking.batchPickingBarcode.toLowerCase().includes(keyword)) {
        return false;
      }
    }
    return true;
  });

  // 處理出庫訂單數據排序
  const sortedAndFilteredOutboundOrders = useMemo(() => {
    let result = [...filteredOutboundOrders];
    
    if (outboundSortField && outboundSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[outboundSortField as keyof OutboundOrder];
        let bValue: any = b[outboundSortField as keyof OutboundOrder];
        
        // 處理數字字段排序
        if (['totalQuantity'].includes(outboundSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理日期字段排序
        if (['scheduledDate'].includes(outboundSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return outboundSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return outboundSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filteredOutboundOrders, outboundSortField, outboundSortOrder]);

  // 處理批量揀貨數據排序
  const sortedAndFilteredBatchPickingLists: BatchPickingList[] = useMemo(() => {
    let result: BatchPickingList[] = [...filteredBatchPickingLists];
    
    if (pickingSortField && pickingSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[pickingSortField as keyof BatchPickingList];
        let bValue: any = b[pickingSortField as keyof BatchPickingList];
        
        // 處理數字字段排序
        if (['totalOrders', 'totalQuantity'].includes(pickingSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理日期字段排序
        if (['scheduledDate', 'createdAt'].includes(pickingSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return pickingSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return pickingSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filteredBatchPickingLists, pickingSortField, pickingSortOrder]);

  // —— 新增：批次列表展開查看該批次下的訂單（grouping by 批次單號）
  const ExpandedOrders: React.FC<{ batchIdOrNumber: string }> = ({ batchIdOrNumber }) => {
    const [rows, setRows] = useState<any[] | null>(null);
    const [loadingOrders, setLoadingOrders] = useState(false);
    useEffect(() => {
      let mounted = true;
      (async () => {
        try {
          setLoadingOrders(true);
          const resp: any = await apiService.getPickingOrders(batchIdOrNumber);
          const list: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
          if (mounted) setRows(list);
        } catch {
          if (mounted) setRows([]);
        } finally { if (mounted) setLoadingOrders(false); }
      })();
      return () => { mounted = false; };
    }, [batchIdOrNumber]);

    const columns: ColumnsType<any> = [
      { title: '訂單號', dataIndex: 'orderNumber', key: 'orderNumber', width: 180 },
      { title: '收件人', dataIndex: 'receiver', key: 'receiver', width: 150 },
      { title: '配送地址', dataIndex: 'deliveryAddress', key: 'deliveryAddress' },
      { title: '快遞商', dataIndex: 'courier', key: 'courier', width: 120 },
      { title: '追蹤號', dataIndex: 'trackingNumber', key: 'trackingNumber', width: 160 },
      { title: '狀態', dataIndex: 'status', key: 'status', width: 120 },
      { title: '數量', dataIndex: 'totalQuantity', key: 'totalQuantity', width: 90, align: 'right' as 'right' },
      { title: '金額', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, align: 'right' as 'right', render: (v: any) => Number(v || 0).toFixed(2) },
      { title: '建立時間', dataIndex: 'createdAt', key: 'createdAt', width: 160, render: (d: any) => moment(d).format('YYYY-MM-DD HH:mm') },
    ];
    return (
      <div style={{ background: '#fafafa', padding: 12 }}>
        <Table
          size="small"
          rowKey={(r: any) => r.id || r.orderNumber}
          dataSource={rows || []}
          columns={columns as any}
          loading={loadingOrders}
          pagination={false}
        />
      </div>
    );
  };

  const renderExpandedBatchRow = (record: BatchPickingList) => {
    const ident = record.id || record.batchPickingNumber;
    return <ExpandedOrders batchIdOrNumber={ident} />;
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <InboxOutlined /> 揀貨管理
        </Title>
        <Text type="secondary">
          管理出庫訂單的揀貨流程，生成批量揀貨單和配貨單
        </Text>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '12px' }}>
        <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k as any)}>
          {showOutboundOrders && user?.role !== 'PICK_SCANNING' && (
            <Tabs.TabPane tab={<span><TruckOutlined /> 出庫訂單</span>} key="orders" />
          )}
          {user?.role !== 'PICK_SCANNING' &&
            <Tabs.TabPane tab={<span><InboxOutlined/> 揀貨單管理</span>} key="picking"/>
          }
          <Tabs.TabPane tab={<span><ScanOutlined /> 掃描操作</span>} key="scanning" />
          {/*<Tabs.TabPane tab={<span><TruckOutlined /> 配貨單管理</span>} key="waybill" />*/}
        </Tabs>
      </div>

      {/* Filters - Orders */}
      {showOutboundOrders && activeTab === 'orders' && (
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Input
              placeholder="訂單編號、客戶"
              prefix={<SearchOutlined />}
              value={filters.searchKeyword}
              onChange={(e) => setFilters({ ...filters, searchKeyword: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="狀態"
              style={{ width: '100%' }}
              mode="multiple"
              value={filters.status}
              onChange={(status) => setFilters({ ...filters, status })}
              allowClear
            >
              <Option value="waiting_shipment">待發貨</Option>
              <Option value="processing">處理中</Option>
              <Option value="picking">配貨中</Option>
              <Option value="checking">核對中</Option>
              <Option value="packed">已打包</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="優先級"
              style={{ width: '100%' }}
              mode="multiple"
              value={filters.priority}
              onChange={(priority) => setFilters({ ...filters, priority })}
              allowClear
            >
              <Option value="low">低</Option>
              <Option value="normal">普通</Option>
              <Option value="high">高</Option>
              <Option value="urgent">緊急</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="銷售渠道"
              style={{ width: '100%' }}
              mode="multiple"
              value={filters.salesChannel}
              onChange={(salesChannel) => setFilters({ ...filters, salesChannel })}
              allowClear
            >
              <Option value="HKTV Mall">HKTV Mall</Option>
              <Option value="Shopify">Shopify</Option>
              <Option value="Amazon">Amazon</Option>
              <Option value="eBay">eBay</Option>
              <Option value="Taobao">Taobao</Option>
              <Option value="Tmall">Tmall</Option>
              <Option value="JD.com">JD.com</Option>
              <Option value="Pinduoduo">Pinduoduo</Option>
              <Option value="Lazada">Lazada</Option>
              <Option value="Shopee">Shopee</Option>
              <Option value="Direct Sales">Direct Sales</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange as any}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates as any })}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => loadData('orders')}
                loading={loading}
              >
                刷新
              </Button>
              <Button
                type="default"
                icon={<FileTextOutlined />}
                onClick={handleExportExcel}
              >
                導出
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      )}

      {/* Filters - Picking */}
      {activeTab === 'picking' && (
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Input
              placeholder="批次編碼、條碼"
              prefix={<SearchOutlined />}
              value={filters.searchKeyword}
              onChange={(e) => setFilters({ ...filters, searchKeyword: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="狀態"
              style={{ width: '100%' }}
              mode="multiple"
              value={filters.status}
              onChange={(status) => setFilters({ ...filters, status })}
              allowClear
            >
              <Option value="processing">處理中</Option>
              <Option value="picking">配貨中</Option>
              <Option value="checking">核對中</Option>
              <Option value="packed">已打包</Option>
              <Option value="delivered">已送達</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange as any}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates as any })}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => loadData('picking')}
                loading={loading}
              >
                刷新
              </Button>
              <Button
                type="default"
                icon={<FileTextOutlined />}
                onClick={handleExportExcel}
              >
                導出
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      )}

      {/* Tab Content */}
      {showOutboundOrders && activeTab === 'orders' && (
        <Card
          title={
            <Space>
              <TruckOutlined />
              <span>出庫訂單列表</span>
              <Badge count={filteredOutboundOrders.length} />
            </Space>
          }
          extra={
            <Space>
              {selectedOrders.length > 0 && (
                <>
                  <Button
                    onClick={() => handleStatusChange(selectedOrders, 'processing')}
                    loading={loading}
                  >
                    標記為處理中 ({selectedOrders.length})
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      // 最終檢查：若選中包含庫存不足的訂單，阻止生成
                      const invalid = outboundOrders.filter(o => selectedOrders.includes(o.id)).some(o => o.hasStockIssues);
                      if (invalid) {
                        message.error('所選訂單包含庫存不足，無法生成揀貨單');
                        return;
                      }
                      setGeneratePickingVisible(true);
                    }}
                    disabled={selectedOrders.length === 0 || outboundOrders.filter(o => selectedOrders.includes(o.id)).some(o => o.hasStockIssues)}
                  >
                    生成揀貨單 ({selectedOrders.length})
                  </Button>
                </>
              )}
              <Dropdown
                overlay={outboundColumnSettingsMenu}
                trigger={['click']}
                open={outboundColumnSettingsVisible}
                onOpenChange={setOutboundColumnSettingsVisible}
              >
                <Button icon={<SettingOutlined />}>
                  列設置
                </Button>
              </Dropdown>
            </Space>
          }
        >
          {selectedOrders.length > 0 && (
            <Alert
              message={`已選擇 ${selectedOrders.length} 個訂單`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
              action={
                <Button size="small" onClick={() => setSelectedOrders([])}>
                  清除選擇
                </Button>
              }
            />
          )}
          
          <Table
            columns={draggableOutboundColumns as any}
            dataSource={sortedAndFilteredOutboundOrders}
            rowKey="id"
            loading={loading}
            pagination={{
              total: outboundTotal,
              current: outboundCurrentPage,
              pageSize: outboundPageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100, 500],
              showTotal: (total, range) => `顯示 ${range[0]}-${range[1]} 項，共 ${total} 項記錄`,
            }}
            onChange={(pagination) => {
              const newSize = pagination.pageSize || outboundPageSize;
              const newPage = pagination.current || 1;
              if (newSize !== outboundPageSize) {
                setOutboundPageSize(newSize);
                setOutboundCurrentPage(1);
              } else {
                setOutboundCurrentPage(newPage);
              }
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      {activeTab === 'picking' && (
        <Card
          title={
            <Space>
              <InboxOutlined />
              <span>批量揀貨單</span>
              <Badge count={batchTotal} />
            </Space>
          }
          extra={
            <Space>
              <Dropdown
                overlay={pickingColumnSettingsMenu}
                trigger={['click']}
                open={pickingColumnSettingsVisible}
                onOpenChange={setPickingColumnSettingsVisible}
              >
                <Button icon={<SettingOutlined />}>
                  列設置
                </Button>
              </Dropdown>
            </Space>
          }
        >
          <Table
            columns={draggableBatchPickingColumns as any}
            dataSource={sortedAndFilteredBatchPickingLists}
            rowKey="id"
            loading={loading}
            expandable={{
              expandedRowRender: (record: any) => renderExpandedBatchRow(record as BatchPickingList),
              rowExpandable: () => true,
            }}
            pagination={{
              total: batchTotal,
              current: batchCurrentPage,
              pageSize: batchPageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100, 500],
              showTotal: (total, range) => `顯示 ${range[0]}-${range[1]} 項，共 ${total} 項記錄`,
            }}
            onChange={(pagination: any) => {
              const newSize = pagination.pageSize || batchPageSize;
              const newPage = pagination.current || 1;
              if (newSize !== batchPageSize) {
                setBatchPageSize(newSize);
                setBatchCurrentPage(1);
              } else {
                setBatchCurrentPage(newPage);
              }
            }}
            scroll={{ x: 1600 }}
          />
        </Card>
      )}

      {activeTab === 'scanning' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Tooltip title="清空掃描歷史">
              <Popconfirm
                title="確認清空掃描歷史？"
                okText="清空"
                cancelText="取消"
                onConfirm={() => setScanHistoryClearKey(k => k + 1)}
              >
                <Button type="default" icon={<DeleteOutlined />}>
                  清空歷史
                </Button>
              </Popconfirm>
            </Tooltip>
          </div>
          <BarcodeScanningInterface
          batchPickingList={
            selectedPickingForScan || {
              id: '',
              batchPickingNumber: '',
              batchPickingBarcode: '',
              status: 'processing',
              scheduledDate: new Date(),
              createdAt: new Date(),
              updatedAt: new Date(),
              outboundOrders: [],
              pickOrderNumbers: [],
              totalOrders: 0,
              totalItems: 0,
              totalQuantity: 0,
              locationFrom: '',
              locationTo: '',
              consolidatedItems: [],
              discrepancies: []
            } as BatchPickingList
          }
          clearHistoryTrigger={scanHistoryClearKey}
          onUpdateBatchPicking={(updated) => {
            setBatchPickingLists(prev => prev.map(bp => {
              if (bp.id !== updated.id) return bp;
              const next: any = { ...bp, ...updated };
              if ((updated as any).consolidatedItems === undefined) {
                next.consolidatedItems = bp.consolidatedItems;
              }
              return next;
            }));
            if (selectedPickingForScan) {
              setSelectedPickingForScan(prev => {
                if (!prev) return prev;
                if (prev.id !== updated.id) return prev;
                const next: any = { ...prev, ...updated };
                if ((updated as any).consolidatedItems === undefined) {
                  next.consolidatedItems = prev.consolidatedItems;
                }
                return next;
              });
            }
            // 在扫描订单完成提交时刷新 KPI
            refreshKpi();
          }}
          onClose={() => {
            setActiveTab('picking');
          }}
          kpi={kpi}
        />
        </>
      )}

      {activeTab === 'waybill' && (
        <WaybillManagement
          batchPickingLists={batchPickingLists}
          onClose={() => setActiveTab('picking')}
        />
      )}

      {/* Generate Picking Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <span>生成批量揀貨單</span>
          </Space>
        }
        open={generatePickingVisible}
        onCancel={() => setGeneratePickingVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          layout="vertical"
          onFinish={handleGenerateBatchPicking}
        >
          <Alert
            message={`即將為 ${selectedOrders.length} 個訂單生成批量揀貨單`}
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="起始位置"
                name="locationFrom"
                initialValue="主倉庫"
              >
                <Select>
                  <Option value="主倉庫">主倉庫</Option>
                  <Option value="副倉庫">副倉庫</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="目標位置"
                name="locationTo"
                initialValue="打包區A"
              >
                <Select>
                  <Option value="打包區A">打包區A</Option>
                  <Option value="打包區B">打包區B</Option>
                  <Option value="打包區C">打包區C</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="備註" name="notes">
            <Input.TextArea rows={3} placeholder="輸入備註信息..." />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setGeneratePickingVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                生成揀貨單
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Picking Detail Modal */}
      <Modal
        title={
          selectedPicking && (
            <Space>
              <FileTextOutlined />
              <span>揀貨單詳情 - {selectedPicking.batchPickingNumber}</span>
            </Space>
          )
        }
        open={pickingDetailVisible}
        onCancel={() => setPickingDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPickingDetailVisible(false)}>
            關閉
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => selectedPicking && handlePrintPickingList(selectedPicking)}
          >
            列印揀貨單
          </Button>
        ]}
        width={1000}
      >
        {selectedPicking && (
          <div>
            {/* Basic Info */}
            <Descriptions
              title="基本信息"
              bordered
              column={2}
              style={{ marginBottom: '24px' }}
            >
              <Descriptions.Item label="批次編號">
                {selectedPicking.batchPickingNumber}
              </Descriptions.Item>
              <Descriptions.Item label="條碼">
                {selectedPicking.batchPickingBarcode}
              </Descriptions.Item>
              <Descriptions.Item label="狀態">
                <Tag color={getStatusColor(selectedPicking.status)}>
                  {getStatusText(selectedPicking.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="計劃日期">
                {moment(selectedPicking.scheduledDate).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="起始位置">
                {selectedPicking.locationFrom}
              </Descriptions.Item>
              <Descriptions.Item label="目標位置">
                {selectedPicking.locationTo}
              </Descriptions.Item>
            </Descriptions>

            {/* Statistics */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={6}>
                <Card>
                  <Statistic title="訂單數量" value={selectedPicking.totalOrders} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="產品項目" value={selectedPicking.totalItems} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="總數量" value={selectedPicking.totalQuantity} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="完成進度" 
                    value={Math.round((selectedPicking.consolidatedItems.reduce((sum, item) => sum + item.confirmedQuantity, 0) / selectedPicking.totalQuantity) * 100)}
                    suffix="%" 
                  />
                </Card>
              </Col>
            </Row>

            {/* Pick Orders */}
            <div style={{ marginBottom: '24px' }}>
              <Title level={5}>包含的揀貨訂單</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedPicking.pickOrderNumbers.map(orderNumber => (
                  <Tag key={orderNumber} color="blue">
                    {orderNumber}
                  </Tag>
                ))}
              </div>
            </div>

            {/* Consolidated Items */}
            <div>
              <Title level={5}>合併產品列表</Title>
              <Table
                dataSource={selectedPicking.consolidatedItems}
                columns={[
                  {
                    title: 'SKU',
                    dataIndex: 'productSku',
                    key: 'productSku',
                    width: 120
                  },
                  {
                    title: '產品名稱',
                    key: 'productName',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{record.productNameEn}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.productNameCn}</div>
                      </div>
                    )
                  },
                  {
                    title: '需求數量',
                    dataIndex: 'totalQuantity',
                    key: 'totalQuantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: '已拣數量',
                    dataIndex: 'pickedQuantity',
                    key: 'pickedQuantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: '確認數量',
                    dataIndex: 'confirmedQuantity',
                    key: 'confirmedQuantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: '位置',
                    key: 'location',
                    width: 120,
                    render: (_, record) => `${record.locationFrom} → ${record.locationTo}`
                  },
                  {
                    title: '批次/序列號',
                    key: 'lotSerial',
                    render: (_, record) => (
                      <div>
                        {record.lotNumbers.length > 0 && (
                          <div style={{ fontSize: '12px' }}>
                            批次: {record.lotNumbers.join(', ')}
                          </div>
                        )}
                        {record.serialNumbers.length > 0 && (
                          <div style={{ fontSize: '12px' }}>
                            序列: {record.serialNumbers.join(', ')}
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    title: '條碼',
                    dataIndex: 'productBarcode',
                    key: 'productBarcode',
                    width: 100,
                    render: (barcode) => (
                      <Tooltip title={barcode}>
                        <BarcodeOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                      </Tooltip>
                    )
                  },
                  {
                    title: '狀態',
                    key: 'status',
                    width: 80,
                    render: (_, record) => {
                      if (record.hasDiscrepancy) {
                        return <Tag color="red">差異</Tag>;
                      } else if (record.confirmedQuantity === record.totalQuantity) {
                        return <Tag color="green">完成</Tag>;
                      } else if (record.pickedQuantity > 0) {
                        return <Tag color="orange">進行中</Tag>;
                      } else {
                        return <Tag color="default">待處理</Tag>;
                      }
                    }
                  }
                ]}
                pagination={false}
                size="small"
                rowKey="productId"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PickingManagement;
